import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Facet, ResourceVm, SearchRecord } from 'qp-ui-interface';
import { takeUntil } from 'rxjs/operators';
import { ClevertapEvent } from '../analytics/clevertap/enum/clevertap.eventType';
import { ClevertapService } from '../analytics/clevertap/service/clevertap.service';
import { AppEventName } from '../analytics/newrelic/enums/EventName';
import { NewrelicService } from '../analytics/newrelic/services/newrelic.service';
import { DataService } from '../data.service';
import { IResourceList } from '../shared/common/interfaces/common.interface';
import { AuthService } from '../shared/services/auth/auth.service';
import { ConfigService } from '../shared/services/config.service';
import { Destroyable } from '../shared/services/destroyable';
import { NavigationLibraryService } from '../shared/services/navigation/navigation.service';
import { ToastService, ToastType } from '../shared/services/toast.service';
import { FocusRestorationService } from '../shared/services/utility/focus-restoration.service';
import { SearchService } from './search.service';
import { RecommendationsService } from '../shared/services/firstlight/recommendations/recommendations.service';

@Component({
  selector: 'app-search-page',
  templateUrl: './search-page.component.html',
  styleUrls: ['./search-page.component.scss'],
})
export class SearchPageComponent
  extends Destroyable
  implements OnInit, OnDestroy
{
  @ViewChild('searchInput') searchInput!: ElementRef;

  // State management
  readonly state = {
    isHover: false,
    isGridFocused: false,
    isSearched: false,
    isSearchedAndNoResult: false,
    searchText: '',
    currentHighlightedKey: '',
    verticalIndex: 0,
    horizontalIndex: 0,
    isLoading: false,
    isRecentSearchAdded: false,
    isFacetFocused: false,
  };

  // Data
  recentSearches: SearchRecord[] = [];
  recentSearchesLoading = false;
  trendingSearches: ResourceVm[] = [];
  resources: ResourceVm[] | null = null;
  facets: Facet | null = null;
  selectedFacetIndex: number = 0;
  focusedFacetIndex: number = -1;
  private pageNumber: number = 1;
  private pageSize: number = 20;
  private totalResources: number = 0;
  isFromKeyboard: boolean = false;

  private readonly MIN_SEARCH_LENGTH = 0;
  private readonly SEARCH_DEBOUNCE_TIME =
    Number(this.configService.applicationConfig.searchDebounceDelay) || 1000;
  private searchTimeout: NodeJS.Timeout | null = null;
  private isPaginating: boolean = false;

  constructor(
    private readonly authService: AuthService,
    private readonly searchService: SearchService,
    private readonly configService: ConfigService,
    private readonly navService: NavigationLibraryService,
    private readonly dataService: DataService,
    private readonly focusRestorationService: FocusRestorationService,
    private readonly newrelic: NewrelicService,
    private readonly clevertapService: ClevertapService,
    private readonly toastService: ToastService,
    private readonly recommendationsService: RecommendationsService
  ) {
    super();
  }

  ngOnInit(): void {
    this.dataService.emitVerticalMenuEvent({ action: 'active' });
    this.initializeSearch();
  }

  ngOnDestroy(): void {
    this.cleanup();
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }

  private initializeSearch(): void {
    this.navService.focusContainer('alphanumeric-keypad', false, 0);
    this.setupGridFocus();
    this.loadRecentSearches();
    this.loadTrendingSearch();
  }

  setupGridFocus() {
    this.dataService.isSearchGridFocused
      .pipe(takeUntil(this.destroy$))
      .subscribe((isSearchGridFocused) => {
        if (isSearchGridFocused !== null) {
          this.state.isGridFocused = isSearchGridFocused;
        }
      });
  }

  onKeyPress(key: string): void {
    this.appendToSearch(key);
    this.scheduleSearch();
  }

  private scheduleSearch(): void {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    const searchTerm = this.state.searchText.trim();
    if (searchTerm.length > this.MIN_SEARCH_LENGTH) {
      this.searchTimeout = setTimeout(() => {
        this.performSearch(searchTerm.toLowerCase());
      }, this.SEARCH_DEBOUNCE_TIME);
    } else {
      this.showTrendingSearch();
    }
  }

  private appendToSearch(char: string): void {
    this.state.isRecentSearchAdded = false;
    if (this.state.searchText.length < 50) {
      this.state.searchText += char.toLowerCase();
    } else {
      this.toastService.show('max_character_limit', ToastType.Warning);
    }
  }

  backspace(): void {
    if (this.state.searchText.length > 0) {
      this.state.searchText = this.state.searchText.slice(0, -1);
      if (this.state.searchText?.length > this.MIN_SEARCH_LENGTH) {
        this.state.isRecentSearchAdded = false;
        this.scheduleSearch();
        return;
      }
    }
    this.clearSearch();
  }

  clearSearch(): void {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    this.state.searchText = '';
    this.state.isRecentSearchAdded = false;
    this.facets = null;
    this.showTrendingSearch();
  }

  addSpace(): void {
    if (!this.state.searchText?.length) {
      return;
    }
    // Only add a space if the last character is not already a space
    if (!this.state.searchText.endsWith(' ')) {
      this.state.searchText += ' ';
      this.scheduleSearch();
    }
  }

  onKeyUp(event: KeyboardEvent): void {
    if (event.isEnter()) {
      const searchTerm = this.state.searchText.trim();
      if (searchTerm.length > this.MIN_SEARCH_LENGTH) {
        if (this.searchTimeout) {
          clearTimeout(this.searchTimeout);
        }
        this.performSearch(searchTerm.toLowerCase());
      }
    }
  }

  onPreviousSearchClick(search: string): void {
    this.state.isRecentSearchAdded = false;
    this.state.searchText = search;
    this.scheduleSearch();
  }

  private async performSearch(
    searchTerm: string,
    facetTerm: string | null = null
  ): Promise<void> {
    if (this.state.isLoading) return;

    try {
      this.state.isLoading = true;
      this.state.isRecentSearchAdded = false;
      this.resetPage();

      if (!facetTerm) {
        this.selectedFacetIndex = 0;
      }
      await this.searchApi(searchTerm, facetTerm);
    } catch (error) {
      this.handleSearchError(error);
    } finally {
      this.state.isLoading = false;
    }
  }

  private async searchApi(searchTerm: string, facetTerm: string | null = null) {
    const result = await this.searchService.getSearchResults(
      searchTerm,
      this.pageNumber.toString(),
      this.pageSize.toString(),
      facetTerm
    );
    this.handleSearchResults(result, searchTerm);
  }

  private handleSearchResults(result: IResourceList, searchTerm: string): void {
    if (result.resources?.length) {
      if (this.pageNumber === 1) {
        this.resources = null;
        this.resources = result.resources;
        // this.updateRecentSearch(searchTerm);
        // Store facets and set default selected facet to "All" on first page
        this.facets = result.facet || null;
        if (this.facets?.terms?.length > 0 && this.selectedFacetIndex < 0) {
          // Set "All" as default selected facet (index 0)
          this.selectedFacetIndex = 0;
        }
      } else {
        this.resources = [...this.resources, ...result.resources];
      }

      this.totalResources = result.totalResources;
      this.state.isSearchedAndNoResult = false;

      for (let i = 0; i < this.resources.length; i++) {
        const resource = this.resources[i];
        resource.containerName = {
          en: 'Top Results',
          te: 'Top Results',
          ta: 'Top Results',
        };
        resource.containerId = 'Top Results';
      }

      // Restore focus to the selected facet after search results appear
      // Only if user was navigating from keyboard (pressed down arrow), not when typing
      if (
        this.pageNumber === 1 &&
        this.facets?.terms?.length > 0 &&
        this.isFromKeyboard
      ) {
        // Use setTimeout to ensure DOM is updated after Angular change detection
        setTimeout(() => {
          this.restoreFacetFocus();
        }, 0);
      }
    } else {
      this.resources = null;
      this.state.isSearchedAndNoResult = true;
      this.facets = null;
    }
    this.state.isSearched = true;
    this.sendSearchEvent(searchTerm, result.totalResources, false, undefined);
  }

  private restoreFacetFocus(): void {
    if (
      this.facets?.terms &&
      this.selectedFacetIndex >= 0 &&
      this.selectedFacetIndex < this.facets.terms.length
    ) {
      // Ensure the facet index is valid (in case facets changed)
      const validIndex = Math.min(
        this.selectedFacetIndex,
        this.facets.terms.length - 1
      );
      this.navService.focusContainer('facets-container', true, validIndex);
      this.state.isFacetFocused = true;
    }
  }

  previousSearchArrowDown(index: number) {
    if (index === this.recentSearches.length - 1) {
      if (this.facets?.terms?.length) {
        const targetIndex =
          this.selectedFacetIndex >= 0 ? this.selectedFacetIndex : 0;
        this.navService.setIndexContainer('facets-container', targetIndex);
        this.navService.focusContainer('facets-container', true, targetIndex);
      } else {
        this.navService.focusContainer('items-wrapper', false, 0);
      }
    }
  }

  private handleSearchError(error: unknown): void {
    this.resources = null;
    this.state.isSearchedAndNoResult = true;
    console.error('Search error:', error);
    // TODO: Add proper error handling/notification here
  }

  private async showTrendingSearch(): Promise<void> {
    this.resources = null;
    if (!this.trendingSearches?.length) {
      this.loadTrendingSearch();
    } else {
      this.resources = this.trendingSearches;
    }
    this.state.isSearched = false;
    this.state.isSearchedAndNoResult = false;
    this.facets = null;
    this.selectedFacetIndex = 0;
  }

  private resetPage(): void {
    this.pageNumber = 1;
    this.totalResources = 0;
  }

  private async loadRecentSearches(): Promise<void> {
    if (this.authService.isLoggedIn()) {
      this.recentSearchesLoading = true;
      try {
        this.recentSearches = await this.searchService.getRecentSearches();
      } catch (error) {
        console.error('Error loading recent searches:', error);
        this.recentSearches = [];
      } finally {
        this.recentSearchesLoading = false;
      }
    }
  }

  private sendSearchEvent(
    searchTerm: string,
    totalItems: number,
    isFreePreview: boolean,
    searchItemPosition: number
  ): void {
    this.newrelic.sendAppEvent(AppEventName.SEARCH, {
      searchString: searchTerm,
      totalItems: totalItems,
      isFreePreview: isFreePreview,
      serachItemPositon: searchItemPosition,
    });
    this.clevertapService.addEvent(ClevertapEvent.SEARCH, {
      searchString: searchTerm,
      isFound: totalItems > 0,
    });
  }

  private async loadTrendingSearch(): Promise<void> {
    const searchData = await this.recommendationsService.fetchTrendingSearch();
    if (searchData && searchData?.resources?.length) {
      this.trendingSearches = searchData.resources;
      this.showTrendingSearch();
    }
  }

  private updateRecentSearch(searchText: string): void {
    if (this.authService.isLoggedIn()) {
      this.searchService
        .addRecentSearch(searchText)
        // .then(() => this.loadRecentSearches())
        .catch((error) =>
          console.error('Error updating recent search:', error)
        );
    }
  }

  get localizedTitle(): string {
    return this.state.isSearched ? 'top_result_title' : 'search.trending.title';
  }

  async paginate(event: any) {
    const keyboardEvent = event as KeyboardEvent;
    if (
      !this.isPaginating &&
      (keyboardEvent.isDown() || keyboardEvent.isRight())
    ) {
      if (
        this.state.isSearched &&
        !this.state.isSearchedAndNoResult &&
        this.totalResources &&
        this.resources.length < this.totalResources
      ) {
        this.isPaginating = true;
        this.pageNumber++;
        // Use index 0 to determine if it's "All" facet (don't send facet term)
        const facetTerm =
          this.selectedFacetIndex === 0
            ? null
            : this.facets?.terms?.[this.selectedFacetIndex]?.term || null;
        await this.searchApi(this.state.searchText, facetTerm);
        this.isPaginating = false;
      }
    }
  }

  // Navigation methods
  fromKeyboardLeft(): void {
    if (this.recentSearches.length > 0) {
      this.navService.focusContainer('previous-searches', false, 0, false);
    } else {
      this.openVerticalMenu();
    }
  }

  fromKeyboardDown(): void {
    // If facets exist, navigate to facets first, otherwise go to grid
    if (this.facets?.terms?.length > 0 && this.state.isSearched) {
      // Navigate to the currently selected facet index, or 0 if none selected
      const targetIndex =
        this.selectedFacetIndex >= 0 ? this.selectedFacetIndex : 0;
      this.navService.setIndexContainer('facets-container', targetIndex);
      this.navService.focusContainer('facets-container', true, targetIndex);
    } else {
      this.navService.focusContainer('items-wrapper', false, 0);
    }
  }

  fromPreviousSearchesDown(): void {
    if (this.facets?.terms?.length > 0 && this.state.isSearched) {
      this.navService.focusContainer('facets-container', false, 0);
    } else {
      this.navService.focusContainer('items-wrapper', false, 0);
    }
  }

  onFacetFocus(term: string, index: number): void {
    this.state.isFacetFocused = true;
    this.focusedFacetIndex = index;
    this.onFacetSelect(term, index);
  }

  onFacetSelect(term: string, index: number): void {
    // Check if this facet is already selected - if so, don't trigger search again
    if (this.selectedFacetIndex === index) {
      return; // Already selected, no need to search again
    }

    // Update the selected facet index
    this.selectedFacetIndex = index;
    this.resetPage();
    const searchTerm = this.state.searchText.trim().toLowerCase();
    if (searchTerm.length > this.MIN_SEARCH_LENGTH) {
      this.performSearch(searchTerm, index === 0 ? null : term);
    }
  }

  fromFacetsDown(): void {
    this.state.isFacetFocused = false;
    this.focusedFacetIndex = -1;
    this.navService.focusContainer('items-wrapper', false, 0);
  }

  fromFacetsUp(): void {
    this.state.isFacetFocused = false;
    this.isFromKeyboard = false;
    this.focusedFacetIndex = -1;
    if (this.recentSearches.length > 0) {
      this.navService.focusContainer(
        'previous-searches',
        false,
        this.recentSearches.length - 1
      );
    } else {
      this.navService.focusContainer('alphanumeric-keypad', false, 0);
    }
  }

  private setGridFocused(isFocused: boolean): void {
    this.dataService.setSearchGridFocused(isFocused);
  }

  isGridFocused(resource: ResourceVm): void {
    if (this.state.isGridFocused !== true) {
      this.setGridFocused(true);
      this.state.isFacetFocused = false;
    }
  }

  fromGridUp(): void {
    this.setGridFocused(false);
    // If facets exist and are visible, navigate to facets first
    if (this.facets?.terms?.length > 0 && this.state.isSearched) {
      // Navigate to the currently selected facet index, or 0 if none selected
      const targetIndex =
        this.selectedFacetIndex >= 0 ? this.selectedFacetIndex : 0;
      this.navService.focusContainer('facets-container', true, targetIndex);
    } else if (this.recentSearches?.length > 0) {
      this.navService.focusContainer(
        'previous-searches',
        false,
        this.recentSearches.length - 1
      );
    } else {
      this.navService.focusContainer('alphanumeric-keypad', false, 0);
      this.isFromKeyboard = false;
    }
  }

  fromGridLeft(): void {
    if (this.state.isGridFocused) {
      //this.setGridFocused(false);
      requestAnimationFrame(() => {
        this.openVerticalMenu();
      });
    }
  }

  openVerticalMenu(focusContainer?: string, index?: number): void {
    if (focusContainer) {
      this.dataService.searchFocus = {
        element: focusContainer,
        index: Number(index),
      };
    }
    this.dataService.emitVerticalMenuEvent({
      action: 'open',
    });
  }

  // used in reuse dont touch
  private restoreFocus(): void {
    this.dataService.emitVerticalMenuEvent({ action: 'active' });
    this.loadRecentSearches();
    this.setupGridFocus();
    this.focusRestorationService.restoreFocusWithFallback(
      '.alphanumeric-keypad',
      {
        containerSelector: '.items-wrapper',
      }
    );
  }

  onClick(resource: ResourceVm): void {
    if (this.state.isSearched && !this.state.isRecentSearchAdded) {
      this.updateRecentSearch(resource.title['en']);
      this.state.isRecentSearchAdded = true;
    }
  }

  trackByIndex(index: number, item: any): number {
    return index;
  }
}
